/**
 * TEN-957 (WP-484 S2) inline per-item relations for `capture action=batch` — extracted out of
 * `handleBatchCapture` (STD-2 / DEC-1504 shrink-only, smart-capture.ts is grandfathered
 * over-budget) so the preview-vs-real branch added here doesn't widen that file's baseline.
 *
 * Fix 2 (WP-577 review re-review, TEN-2918 follow-up): a preview batch's inline relations
 * always reported themselves as FAILED. `chain.createEntryRelation`
 * (convex/agentKnowledge/relations.ts:69-80) resolves `fromEntry` from the `entries` table and
 * throws `notFound` BEFORE any preview branch in that mutation runs. In a preview batch the
 * source entry (`finalEntryId`) was never persisted — `createEntry`'s own preview branch never
 * writes a doc — so every inline relation call was GUARANTEED to hit that NOT_FOUND, even
 * though the real (non-preview) capture creates the source first and links successfully
 * afterward. Reporting that as a failure told the caller a relation that WOULD succeed on a
 * real capture is broken.
 *
 * The fix (as first shipped): never call `chain.createEntryRelation` for a preview batch's
 * inline relations at all — count them as would-be-created instead, mirroring the
 * `batchAutoLinks` preview handling already in `handleBatchCapture` for the same underlying
 * reason.
 *
 * Fix 4 (WP-577 review round 3, TEN-2918 follow-up): that first fix replaced one lie with the
 * opposite lie. Counting every schema-valid `{to, type}` pair as "would be created" PROMISES an
 * edge with no target-existence check, no `governs` authorization check, and no `guardRelation`
 * policy check — the real (non-preview) path performs all three
 * (convex/agentKnowledge/relations.ts), so a missing target or a forbidden relation type can
 * fail, or resolve to an agent proposal instead of a write, even though the preview said it would
 * simply be created. This module deliberately does NOT attempt to reimplement target/policy
 * validation client-side (that is kernel logic — STD-2's connector boundary forbids it here, and
 * TEN-2278 already establishes connectors cannot import kernel/convex logic). The honest fix is
 * to under-promise: report this cohort as NOT VALIDATED in preview, never as would-succeed.
 */
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { kernelMutation } from "../client.js";

export type BatchInlineRelation = { to: string; type: string };

export type BatchInlineRelationsResult = {
  relationsCreatedCount: number;
  relationsProposedCount: number;
  // Fix 4 (TEN-2918 follow-up): named for what preview honestly knows — NOT for a predicted
  // outcome. Renamed from `relationsPreviewedCount`, which the caller-facing warning text read
  // as a promise ("would be created"). No target-existence check, no `governs` authorization
  // check, and no `guardRelation` policy check has run against this cohort — those are the real
  // path's kernel-side checks (convex/agentKnowledge/relations.ts), never reproduced here.
  relationsNotValidatedCount: number;
  relationsFailedList: string[];
};

/**
 * Applies (or, under `preview`, merely counts) one batch entry's inline relations.
 * `finalEntryId` is the entry these relations attach FROM — real on a non-preview create,
 * predicted-only (never persisted) on a preview one.
 */
export async function applyBatchInlineRelations(
  relations: BatchInlineRelation[] | undefined,
  finalEntryId: string,
  preview: boolean | undefined,
  agentId: string | null,
): Promise<BatchInlineRelationsResult> {
  const result: BatchInlineRelationsResult = {
    relationsCreatedCount: 0,
    relationsProposedCount: 0,
    relationsNotValidatedCount: 0,
    relationsFailedList: [],
  };
  if (!relations || relations.length === 0) return result;

  if (preview) {
    // No write, no mutation call — see module doc comment for why this cohort can never
    // resolve `fromEntry` on a preview batch. Fix 4: NOT a promise these would be created —
    // target existence and relation policy are unchecked here; see the type's doc comment.
    result.relationsNotValidatedCount = relations.length;
    return result;
  }

  for (const rel of relations) {
    try {
      // Finding #14 (pre-existing): forward the returned status instead of discarding it — a
      // discarded result treated every non-throwing call as "created", including misuse-pattern
      // interceptions that only file an agent proposal (no edge written).
      const relResult = await kernelMutation<GatewayRouteReturnByName<"chain.createEntryRelation">>(
        "chain.createEntryRelation",
        {
          fromEntryId: finalEntryId,
          toEntryId: rel.to,
          type: rel.type,
          proposedBy: "user",
          sessionId: agentId ?? undefined,
        },
      );
      if (relResult?.status === "agent_proposal_created") {
        result.relationsProposedCount++;
      } else {
        result.relationsCreatedCount++;
      }
    } catch (relErr: unknown) {
      const relMsg = relErr instanceof Error ? relErr.message : String(relErr);
      result.relationsFailedList.push(`${rel.to} (${rel.type}): ${relMsg}`);
    }
  }
  return result;
}
